import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Job recommendations emailed to graduates, served by the jobs service
// through the gateway at /jobs/admin/*.
const BASE = '/jobs/admin';
const KEY = ['job-digest'];
const quiet = { refetchOnWindowFocus: false, retry: false };

const cohortParams = (cohortIds) =>
  cohortIds?.length ? { cohort_ids: cohortIds.join(',') } : {};

export const useJobCohorts = () =>
  useQuery(
    [...KEY, 'cohorts'],
    async () =>
      (await axiosInstancev2.get(`${BASE}/cohorts`)).data?.cohorts ?? [],
    { ...quiet, staleTime: 5 * 60 * 1000 }
  );

export const useCampaigns = () =>
  useQuery(
    [...KEY, 'campaigns'],
    async () =>
      (await axiosInstancev2.get(`${BASE}/campaigns`)).data?.campaigns ?? [],
    {
      ...quiet,
      // Keep polling while a campaign is still going.
      refetchInterval: (data) =>
        data?.some((c) => c.status === 'queued' || c.status === 'running')
          ? 5000
          : false,
    }
  );

// body: { cohort_ids, include_members }
export const usePreviewCampaign = () =>
  useMutation(
    async (body) =>
      (
        await axiosInstancev2.post(`${BASE}/campaigns/preview`, body, {
          timeout: 120 * 1000,
        })
      ).data
  );

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (body) =>
      (await axiosInstancev2.post(`${BASE}/campaigns`, body)).data?.campaign,
    { onSuccess: () => queryClient.invalidateQueries(KEY) }
  );
};

export const useRecommendations = (filters) =>
  useQuery(
    [...KEY, 'recommendations', filters],
    async () =>
      (
        await axiosInstancev2.get(`${BASE}/recommendations`, {
          params: {
            ...cohortParams(filters.cohortIds),
            campaign_id: filters.campaignId || undefined,
            status: filters.status || undefined,
            q: filters.q || undefined,
            limit: filters.limit,
            offset: filters.offset,
          },
        })
      ).data,
    { ...quiet, keepPreviousData: true }
  );

export const useStudentJobs = (studentId, filters) =>
  useQuery(
    [...KEY, 'student', studentId, filters],
    async () =>
      (
        await axiosInstancev2.get(`${BASE}/recommendations/${studentId}`, {
          params: {
            ...cohortParams(filters.cohortIds),
            campaign_id: filters.campaignId || undefined,
            status: filters.status || undefined,
          },
        })
      ).data,
    { ...quiet, enabled: Boolean(studentId) }
  );

export const useApplications = (filters) =>
  useQuery(
    [...KEY, 'applications', filters],
    async () =>
      (
        await axiosInstancev2.get(`${BASE}/applications`, {
          params: {
            ...cohortParams(filters.cohortIds),
            status: filters.status,
            include_test: filters.includeTest ? 1 : undefined,
            limit: filters.limit,
            offset: filters.offset,
          },
        })
      ).data,
    { ...quiet, keepPreviousData: true }
  );

export const useReviewApplication = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, status, note }) =>
      (
        await axiosInstancev2.post(`${BASE}/applications/${id}/review`, {
          status,
          note,
        })
      ).data?.application,
    { onSuccess: () => queryClient.invalidateQueries(KEY) }
  );
};

// Proof images need the auth token, so they come through as blobs.
export const useProofImage = (applicationId) =>
  useQuery(
    [...KEY, 'proof', applicationId],
    async () => {
      const response = await axiosInstancev2.get(
        `${BASE}/applications/${applicationId}/proof`,
        { responseType: 'blob' }
      );
      return URL.createObjectURL(response.data);
    },
    { ...quiet, enabled: Boolean(applicationId), staleTime: Infinity }
  );

export const useReportSummary = (filters) =>
  useQuery(
    [...KEY, 'summary', filters],
    async () =>
      (
        await axiosInstancev2.get(`${BASE}/reports/summary`, {
          params: {
            ...cohortParams(filters.cohortIds),
            campaign_id: filters.campaignId || undefined,
          },
        })
      ).data,
    { ...quiet, enabled: filters.cohortIds?.length > 0 }
  );

const readBlobError = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.error;
    } catch {
      return null;
    }
  }
  return data?.error;
};

// report is 'students' or 'recommendations'.
export async function downloadJobsCsv(report, filters) {
  try {
    const response = await axiosInstancev2.get(
      `${BASE}/reports/${report}.csv`,
      {
        params: {
          ...cohortParams(filters.cohortIds),
          campaign_id: filters.campaignId || undefined,
        },
        responseType: 'blob',
        timeout: 120 * 1000,
      }
    );
    const header = response.headers?.['content-disposition'] || '';
    const name =
      /filename="([^"]+)"/.exec(header)?.[1] || `job-digest-${report}.csv`;
    saveAs(response.data, name);
  } catch (error) {
    throw new Error(
      (await readBlobError(error)) ||
        `Download failed${
          error?.response?.status ? ` (${error.response.status})` : ''
        }.`
    );
  }
}

export const jobsError = (error, fallback) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.error;
  if (status === 403) {
    return (
      message ||
      'You need the ManageJobDigest permission to manage job recommendations.'
    );
  }
  if (status === 404 && !message) {
    return "Job recommendations aren't available on the server yet.";
  }
  if (status === 502 || status === 503) {
    return 'The jobs service is unreachable right now.';
  }
  return message || fallback;
};
